import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { X, ShieldCheck, Copy, Download, RefreshCw, AlertTriangle, Check, Eye, EyeOff, KeyRound } from 'lucide-react';

/**
 * Full 2FA setup + management wizard.
 *
 * States:
 *   'idle'      → 2FA not enabled; show "Enable 2FA" CTA
 *   'password'  → asking for current password (required to start setup)
 *   'qr'        → showing QR + manual key, asking for verification code
 *   'backup'    → showing the freshly generated 10 backup codes ONCE
 *   'enabled'   → 2FA active; show status + manage buttons
 *
 * Additional flows: regenerate backup codes, disable 2FA (password + code).
 */
export default function TwoFactorSetup({ onChange }) {
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [flow, setFlow] = useState('idle');
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [setupData, setSetupData] = useState(null);   // {secret, otpauth_uri}
  const [backupCodes, setBackupCodes] = useState([]); // shown ONCE
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await http.get('/auth/2fa/status');
      setStatus(data);
      setFlow(data.enabled ? 'enabled' : 'idle');
    } catch (e) { /* noop */ }
  };
  useEffect(() => { refresh(); }, []);

  const startSetup = () => { setFlow('password'); setPw(''); setCode(''); };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post('/auth/2fa/setup', { password: pw });
      setSetupData(data);
      setFlow('qr');
      setCode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start 2FA setup.');
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post('/auth/2fa/enable', { code });
      setBackupCodes(data.backup_codes || []);
      setFlow('backup');
      onChange?.();
      toast.success('2FA enabled — save your backup codes!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code — try the next one.');
    } finally { setLoading(false); }
  };

  const acknowledgeBackup = async () => {
    setBackupCodes([]); setSetupData(null); setPw(''); setCode('');
    await refresh();
  };

  const regenerate = async () => {
    const password = window.prompt('Confirm your password to regenerate backup codes:');
    if (!password) return;
    const c = window.prompt('Enter a fresh 6-digit 2FA code from your authenticator:');
    if (!c) return;
    try {
      const { data } = await http.post('/auth/2fa/backup-codes/regenerate', { password, code: c });
      setBackupCodes(data.backup_codes || []);
      setFlow('backup');
      toast.success('Backup codes regenerated — save the new set.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not regenerate codes.');
    }
  };

  const disable2FA = async () => {
    const password = window.prompt('Confirm your password to disable 2FA:');
    if (!password) return;
    const c = window.prompt('Enter a fresh 6-digit 2FA code (or a backup code):');
    if (!c) return;
    if (!window.confirm('This will disable 2FA AND sign out every trusted device. Continue?')) return;
    try {
      await http.post('/auth/2fa/disable', { password, code: c });
      toast.success('2FA disabled.');
      setFlow('idle');
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not disable 2FA.');
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard.');
  };
  const downloadBackupCodes = () => {
    const blob = new Blob([
      'Brotherhood+ 2FA Backup Codes\n',
      `Generated: ${new Date().toISOString()}\n`,
      '\nUse each code ONCE if you lose access to your authenticator.\n\n',
      backupCodes.map((c, i) => `${(i + 1).toString().padStart(2, '0')}. ${c}`).join('\n'),
      '\n\nKeep these codes as safe as your password.\n',
    ], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `brotherhood-plus-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="bg-white rounded-3xl p-6 card-soft" data-testid="twofactor-card">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${status.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-beige text-navy/60'}`}>
          <ShieldCheck size={20}/>
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg">Two-Factor Authentication (2FA)</h3>
          <p className="text-sm text-navy/60 mt-0.5">
            {status.enabled
              ? <>Enabled. <span className="text-emerald-700 font-medium">{status.backup_codes_remaining} backup code{status.backup_codes_remaining === 1 ? '' : 's'} remaining.</span></>
              : 'Add a time-based code on top of your password for enterprise-grade protection.'}
          </p>
        </div>
      </div>

      {flow === 'idle' && (
        <button onClick={startSetup}
          className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth"
          data-testid="twofactor-enable">
          Enable 2FA
        </button>
      )}

      {flow === 'password' && (
        <form onSubmit={submitPassword} className="space-y-3" data-testid="twofactor-password-step">
          <label className="text-xs uppercase tracking-widest text-navy/60">Current password</label>
          <input type="password" required autoFocus value={pw} onChange={e=>setPw(e.target.value)}
            className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="twofactor-password-input"/>
          <button type="submit" disabled={loading}
            className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth disabled:opacity-50">
            {loading ? 'Verifying…' : 'Continue'}
          </button>
        </form>
      )}

      {flow === 'qr' && setupData && (
        <div className="space-y-4" data-testid="twofactor-qr-step">
          <p className="text-sm text-navy/70 leading-relaxed">
            Scan this QR code with Google Authenticator, 1Password, Authy, or any TOTP app — then enter the 6-digit code it shows.
          </p>
          <div className="bg-white rounded-2xl p-6 flex items-center justify-center border border-navy/10">
            <QRCodeSVG value={setupData.otpauth_uri} size={200} bgColor="#FFFFFF" fgColor="#0B192C" level="M"/>
          </div>
          <div className="text-xs text-navy/55 space-y-1">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-widest">Manual key</span>
              <button onClick={()=>setShowSecret(s=>!s)} className="text-navy/60 hover:text-navy" aria-label="Toggle secret visibility">
                {showSecret ? <EyeOff size={12}/> : <Eye size={12}/>}
              </button>
              <button onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success('Copied.'); }}
                className="text-navy/60 hover:text-navy ml-auto flex items-center gap-1"><Copy size={12}/> Copy</button>
            </div>
            <code className="block bg-beige rounded-lg px-3 py-2 font-mono text-navy/80 select-all">
              {showSecret ? setupData.secret.match(/.{1,4}/g)?.join(' ') : '•••• •••• •••• •••• •••• •••• ••••'}
            </code>
          </div>
          <form onSubmit={submitCode} className="space-y-3">
            <label className="text-xs uppercase tracking-widest text-navy/60">Enter the 6-digit code</label>
            <input inputMode="numeric" pattern="[0-9]*" required autoFocus
              value={code} onChange={e=>setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold tracking-[0.4em] font-mono text-center text-lg"
              data-testid="twofactor-code-input"/>
            <button type="submit" disabled={loading || code.length !== 6}
              className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth disabled:opacity-50"
              data-testid="twofactor-verify-submit">
              {loading ? 'Verifying…' : 'Verify & enable'}
            </button>
          </form>
        </div>
      )}

      {flow === 'backup' && backupCodes.length > 0 && (
        <div className="space-y-4" data-testid="twofactor-backup-step">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-amber-900">
            <p className="flex items-start gap-2 text-sm font-medium">
              <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
              Save these 10 backup codes NOW. Each is single-use, and you will never see them again.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c, i) => (
              <div key={i} className="bg-beige rounded-lg px-3 py-2 select-all" data-testid={`backup-code-${i}`}>
                <span className="text-navy/40 mr-2">{(i + 1).toString().padStart(2, '0')}</span>{c}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={copyBackupCodes} className="flex-1 bg-beige text-navy rounded-full py-2.5 text-sm font-medium hover:bg-beige/70 flex items-center justify-center gap-2"><Copy size={14}/> Copy</button>
            <button onClick={downloadBackupCodes} className="flex-1 bg-beige text-navy rounded-full py-2.5 text-sm font-medium hover:bg-beige/70 flex items-center justify-center gap-2"><Download size={14}/> Download .txt</button>
          </div>
          <button onClick={acknowledgeBackup} className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth flex items-center justify-center gap-2"
            data-testid="twofactor-backup-done">
            <Check size={14}/> I&apos;ve saved my codes
          </button>
        </div>
      )}

      {flow === 'enabled' && backupCodes.length === 0 && (
        <div className="space-y-2" data-testid="twofactor-manage">
          <button onClick={regenerate} className="w-full text-left rounded-2xl bg-beige/70 hover:bg-beige px-4 py-3 flex items-center gap-3 text-navy transition-smooth" data-testid="twofactor-regenerate">
            <RefreshCw size={16}/> <span className="flex-1"><span className="font-medium">Regenerate backup codes</span><br/><span className="text-xs text-navy/55">Invalidates the previous set</span></span>
          </button>
          <button onClick={disable2FA} className="w-full text-left rounded-2xl bg-red-50 hover:bg-red-100 px-4 py-3 flex items-center gap-3 text-red-700 transition-smooth" data-testid="twofactor-disable">
            <KeyRound size={16}/> <span className="flex-1"><span className="font-medium">Disable 2FA</span><br/><span className="text-xs text-red-700/70">Removes the extra login step + revokes trusted devices</span></span>
          </button>
        </div>
      )}
    </div>
  );
}
